/**
 * Get Started checklist API service.
 *
 * The checklist is decoration on a page that works fine without it, so every call here is written
 * to let the caller degrade quietly rather than to surface an error.
 */

import { get, post, del, ApiError } from "./api";
import type { GetStartedChecklist } from "../types/onboarding";
import { notifyGetStartedChanged } from "./onboardingEvents";

export interface GetStartedChecklistOptions {
  /**
   * Ask for the completion set even when this user has hidden the panel.
   *
   * Off by default, and that default is the whole point. This read runs on `/players` — the landing
   * route — for every user on every visit, and the panel renders nothing once it has been
   * dismissed, so the server skips the completion queries entirely unless a caller says it will
   * render them. Only the organization settings card needs them regardless, because it reports what
   * is left to do whether or not the panel is hidden.
   *
   * A user who has **not** dismissed the panel gets the items either way: the server short-circuits
   * on the dismissal, not on this flag.
   */
  includeItems?: boolean;
  signal?: AbortSignal;
}

/**
 * What this tab has already been told, keyed by whether the answer carries the completion set.
 *
 * Keyed rather than shared, because the two are genuinely different answers. A response fetched
 * without `includeItems` carries `items: null` for a dismissed user, which means "not asked" — hand
 * that to the settings card and it loses the one distinction it exists to make, between a program
 * that has finished every step and one that merely hid the panel.
 *
 * Only settled successes are stored, never the promise. Caching the promise would dedupe the
 * StrictMode double-mount as well, but the first of that pair is aborted by its own cleanup, so
 * every later caller would inherit that rejection and render nothing.
 */
const cache = new Map<boolean, GetStartedChecklist | null>();

/**
 * Bumped by every invalidation, so a read that was already in flight when the cache was cleared can
 * tell that its answer is stale and decline to store it.
 *
 * Without it, clearing is not actually enough: sign-out clears synchronously, but the departing
 * user's request is still out, and it lands *after* — repopulating the cache it was meant to empty,
 * with the previous user's checklist, in a tab the next user is about to sign into. The in-flight
 * caller still gets its own answer; it just does not get to leave it behind.
 */
let generation = 0;

/**
 * Forget everything cached above.
 *
 * Called on dismiss and on restore, which change the answer for this user, and on the two events
 * that change *whose* answer it is:
 *
 * - **sign-out**, or the next user in the same tab inherits the previous one's checklist, since
 *   this cache is module scope and outlives the React tree that filled it.
 * - **switching or clearing an impersonation**, because completion is organization-wide and
 *   `api.ts` scopes every request by the `X-Organization-Id` header. Nothing on that path reloads
 *   the page, so an admin moving to a brand-new organization would otherwise read the one they
 *   left and see its setup already ticked off.
 *
 * That last one is invalidation by call site rather than by cache key, which is correct only while
 * `switchOrganization` and `clearImpersonation` remain the only writers of the localStorage key
 * `api.ts` reads the effective organization from. Anything new that pins an organization — a deep
 * link, say — has to clear this too, and that is the point at which keying the cache by
 * organization id becomes the better shape.
 */
export const clearGetStartedChecklistCache = (): void => {
  generation++;
  cache.clear();
};

/**
 * The caller's checklist, or **null when this API does not have the endpoint**.
 *
 * Served from a per-tab cache after the first successful read. `PlayersSection` unmounts on every
 * route change, so a coach moving between the players list and a prospect twenty times a day paid
 * twenty round trips for a panel that, for any established program, renders nothing.
 *
 * The staleness this buys is deliberate and bounded: a task the program finished elsewhere ticks
 * off on the next full load rather than mid-session. Invalidation is wired to the things that change
 * the answer for this user (dismiss, restore) and to the things that change *whose* answer it is
 * (sign-out, switching or clearing an impersonation) — and not to data mutations, which would be a
 * cache invalidated from every corner of the app for a panel nobody is looking at.
 *
 * Web and API deploy on separate workflows, so a web build reaching an older API is an ordinary
 * state, not a failure. A 404 there means "no checklist feature", and the panel renders nothing.
 * Every other failure still throws, so a real outage is not disguised as an old API — and nothing
 * but a settled answer is cached, so a timeout or an aborted unmount is retried on the next mount
 * rather than remembered as "nothing to show".
 *
 * The one sharp edge left: a 404 that is *not* an older API — an SPA or CDN fallback during a bad
 * deploy window, say — is remembered for the life of the tab, so a user who loaded in that window
 * gets no checklist until they reload. It is the only route to a panel that stays hidden, and it is
 * survivable because this is decoration, but it is the thing to suspect if one ever goes missing.
 */
export const getGetStartedChecklist = async (
  { includeItems = false, signal }: GetStartedChecklistOptions = {}
): Promise<GetStartedChecklist | null> => {
  // `has` rather than a truthiness check: null is a real cached answer here (an API without the
  // endpoint), and re-asking for it every mount is exactly the cost this cache removes.
  if (cache.has(includeItems)) return cache.get(includeItems) ?? null;

  // Read before the request goes out, compared after it lands. Anything that invalidated in between
  // means this answer describes a user, or an organization, we have since left.
  const issued = generation;

  try {
    const result = await get<GetStartedChecklist>(
      `/api/onboarding/checklist${includeItems ? "?includeItems=true" : ""}`,
      { signal }
    );
    if (issued === generation) cache.set(includeItems, result);
    return result;
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 404) {
      if (issued === generation) cache.set(includeItems, null);
      return null;
    }
    throw error;
  }
};

/** Hides the panel for this user only. Answers with the updated checklist. */
export const dismissGetStarted = async (): Promise<GetStartedChecklist> => {
  try {
    return await post<GetStartedChecklist>("/api/onboarding/dismiss");
  } finally {
    // Cleared even when the write reports a failure. A request that errored after the server had
    // already committed would otherwise leave this tab serving a checklist the API disagrees with,
    // for the rest of the session. The cost of being wrong the other way is one request.
    clearGetStartedChecklistCache();
    // And tell the copies already on screen, which the cleared cache alone would not reach.
    notifyGetStartedChanged();
  }
};

/**
 * Brings the panel back for this user. Answers with the updated checklist.
 *
 * Called from the sidebar's Get Started dialog and from the settings card. Either can run while the
 * panel is mounted behind it, which is why this notifies as well as clearing the cache.
 */
export const restoreGetStarted = async (): Promise<GetStartedChecklist> => {
  try {
    return await del<GetStartedChecklist>("/api/onboarding/dismiss");
  } finally {
    clearGetStartedChecklistCache();
    notifyGetStartedChanged();
  }
};
